import { useSignOut } from '@/hooks/use-sign-out';
import Button from '@components/base/button.component.tsx';
import Container from '@components/base/container.component.tsx';
import { ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  colors,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import eLinkLogo from '../../../assets/elink_distribution_ag_logo_white.svg';
import { useCustomerPages } from '@/config/external-customer-pages';
import { useCustomerSettings } from '@/config/external-customer-settings';
import { useCart } from '@/api/carts/carts.hooks.ts';
import { LanguageSwitcher } from '@/components/language-switcher';
import { useTranslation } from 'react-i18next';

function ShoppingCart() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { data: cartData } = useCart();
  const cartCount = useMemo(() => cartData?.cartItems.length || 0, [cartData]);
  return (
    <Tooltip title={t('tooltips.openCart')}>
      <IconButton
        size="large"
        color="inherit"
        onClick={() => navigate('/bp/c/cart')}
      >
        <Badge badgeContent={cartCount} color={'error'}>
          <ShoppingCartIcon />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}

export function CustomerAppBar() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const location = useLocation();
  const signOut = useSignOut();
  const pages = useCustomerPages();
  const settings = useCustomerSettings();
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleNavigate = (url: string) => {
    navigate(url);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        zIndex: 20,
        maxHeight: '64px',
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          px: {
            xs: 0,
            md: 3,
          },
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
          disableGutters
        >
          <Box
            style={{
              height: '28px',
              marginRight: 24,
            }}
            sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}
          >
            {/*{isItems && !isMobile && (*/}
            {/*  <ToggleItemGroupListButton*/}
            {/*    sx={{*/}
            {/*      position: "absolute",*/}
            {/*      ml: -5,*/}
            {/*    }}*/}
            {/*  />*/}
            {/*)}*/}
            <img
              src={eLinkLogo}
              alt="eLink Distribution AG"
              className={'mx-auto'}
              style={{
                height: '28px',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            />
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            {/*{isItems && isMobile && <ToggleItemGroupListButton />}*/}
            <IconButton
              size="large"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={anchorElNav !== null}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {pages.map((page) => (
                <MenuItem
                  key={page.title}
                  onClick={() => {
                    handleCloseNavMenu();
                    handleNavigate(page.url);
                  }}
                >
                  {page.icon}
                  <Typography sx={{ textAlign: "center", ml: 2 }}>
                    {page.title}
                  </Typography>
                  {page.node}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Box
            sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 2 }}
          >
            {pages.map((page) => (
              <Button
                variant={'text'}
                startIcon={page.icon}
                key={page.title}
                onClick={() => {
                  handleCloseNavMenu();
                  handleNavigate(page.url);
                }}
                sx={{
                  my: 2,
                  color:
                    location.pathname !== page.url
                      ? 'white'
                      : colors.blueGrey[200],
                }}
              >
                {page.title}
                {page.node}
              </Button>
            ))}
          </Box>
          <Box sx={{ flexGrow: 0, paddingLeft: 1.5, display: 'flex' }}>
            {/*<SyncStatus />*/}
            <ShoppingCart />
            <LanguageSwitcher />
            <Tooltip title={t('tooltips.openSettings')}>
              <IconButton onClick={handleOpenUserMenu} sx={{ pl: 2 }}>
                <Avatar alt="Remy Sharp" src="/static/images/avatar/2.jpg" />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map((setting) => (
                <MenuItem
                  key={setting.label}
                  onClick={() => {
                    handleCloseUserMenu();
                    if (setting.href) {
                      navigate(setting.href);
                    }
                  }}
                >
                  <Typography sx={{ textAlign: "center" }}>{setting.label}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
